import { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Button,
  Avatar,
  styled,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  Person,
  Schedule,
  Logout,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearUser } from "@app/store/auth/auth.action";
import StorageService from "@core/services/storage";

const StyledAppBar = styled(AppBar)(() => ({
  backgroundColor: "#fff",
  color: "#000",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "0 24px",
  minHeight: "64px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    minHeight: "56px",
  },
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  "& h6": {
    fontSize: "20px",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
}));

const NavSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "32px",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const UserSection = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
  },
}));

const StyledDrawer = styled(Drawer)(() => ({
  "& .MuiDrawer-paper": {
    width: "240px",
    padding: "16px 0",
  },
}));

const navItems = [
  { text: "Home", href: "/" },
  { text: "Practice", href: "/practice" },
  { text: "Tests", href: "/tests" },
  { text: "Results", href: "/results" },
];

const staticMenuItems = [
  { text: "Notifications", icon: <Notifications />, href: "/notifications" },
  { text: "Schedule", icon: <Schedule />, href: "/schedule" },
  { text: "Profile", icon: <Person />, href: "/profile" },
  { text: "Logout", icon: <Logout />, type: "logout" },
];

function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    dispatch(clearUser());
    StorageService.remove("access_token");
    StorageService.remove("role");
    StorageService.removeSession("access_token");
    StorageService.removeSession("role");
    navigate("/login");
  };

  const menuItems = staticMenuItems.map((item) => ({
    ...item,
    onClick: item.type === "logout" ? handleLogout : undefined,
  }));

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <LogoSection>
          <MobileMenuButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleMobileMenuToggle}
          >
            <MenuIcon />
          </MobileMenuButton>
          <Avatar src="/logo.jpg" variant="square">
          </Avatar>
          <Typography variant="h6">

            IELTS Practice
          </Typography>
        </LogoSection>

        <NavSection>
          {navItems.map((item) => (
            <Button
              key={item.text}
              color="inherit"
              href={item.href}
              sx={{
                fontSize: "16px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              {item.text}
            </Button>
          ))}
        </NavSection>

        <UserSection>
          <Avatar
            onClick={handleMenuOpen}
            aria-controls={anchorEl ? "user-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? "true" : undefined}
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              cursor: "pointer",
              backgroundColor: "#1976d2",
            }}
          >
            JD
          </Avatar>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "user-menu-button",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 200,
              },
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }

                  handleMenuClose();
                }}
                component={item.href ? "a" : "button"}
                href={item.href}
                sx={{
                  gap: "12px",
                  py: 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item.icon}
                <Typography>{item.text}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </UserSection>
      </StyledToolbar>

      <StyledDrawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
      >
        <List>
          {navItems.map((item) => (
            <ListItem
              key={item.text}
              component="a"
              href={item.href}
              onClick={handleMobileMenuToggle}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: { fontSize: "16px" },
                }}
              />
            </ListItem>
          ))}
        </List>
      </StyledDrawer>
    </StyledAppBar>
  );
}

export default Header;
